import React, { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Vertical drag handle that resizes a sibling panel by its width.
 *
 * This is a Tailwind-only implementation matching the medstudent clinic
 * design. The doctor page's ResizableSplitter (Chakra + DOM queries against
 * `.main-content-area`) would have been heavier than needed here — we just
 * want to drag the boundary between the document and the right panel.
 *
 * The parent owns the width state and persistence; this component is purely
 * the draggable affordance.
 */
export default function ResizeSplitter({
  // Called with the new width (in px) on every drag tick. Parent clamps and
  // persists; we just translate mouse-x → desired width.
  onResize,
  // Drag updates the panel that lives to the RIGHT of the splitter. The
  // formula is `windowWidth - clientX` for a right panel; pass side="left"
  // to invert (clientX - leftOffset).
  side = 'right',
  // px bounds — values outside this range are clamped at the parent.
  minWidth = 280,
  maxWidth = 720,
}) {
  const draggingRef = useRef(false);
  // Mirrored to React state so we can conditionally render the drag
  // overlay below — that's the piece that fixes the iframe issue.
  const [isDragging, setIsDragging] = useState(false);

  // Body cursor + select suppression while dragging — without this the
  // browser keeps showing the I-beam over text and selects content under
  // the cursor as the user drags across the document. Include the WebKit
  // prefix since Chromium honours it more reliably than the unprefixed
  // form on text inside buttons (the tab labels).
  const setDragCursor = useCallback((on) => {
    document.body.style.cursor = on ? 'ew-resize' : '';
    document.body.style.userSelect = on ? 'none' : '';
    document.body.style.webkitUserSelect = on ? 'none' : '';
    // Belt-and-suspenders: if a selection already exists when drag starts,
    // collapse it so the highlight doesn't visually persist through the
    // drag and look like the splitter caused it.
    if (on && window.getSelection) {
      try { window.getSelection().removeAllRanges(); } catch (_) { /* fine */ }
    }
  }, []);

  // Bounds + onResize are read via refs INSIDE the listeners so the
  // listener identities stay stable for the lifetime of the splitter.
  // Without this, the parent updating `maxWidth` mid-drag (which is
  // exactly what the doc-area overflow detector does the instant the
  // header hits its limit) regenerated onMouseMove/onMouseUp, triggered
  // the cleanup effect, and ripped the window listeners off without
  // re-attaching — so the final mouseup never fired and the body cursor
  // got stuck in ew-resize until the next click.
  const cfgRef = useRef({ onResize, side, minWidth, maxWidth });
  useEffect(() => {
    cfgRef.current = { onResize, side, minWidth, maxWidth };
  }, [onResize, side, minWidth, maxWidth]);

  // Stable refs to the listeners themselves — assigned once on mount.
  const onMouseMoveRef = useRef(null);
  const onMouseUpRef = useRef(null);
  // We need a stable reference to a function that releases the listeners
  // (used by both onMouseUp and the unmount cleanup). It also can't depend
  // on the listeners themselves, since those live in refs.
  const releaseRef = useRef(null);

  if (!onMouseMoveRef.current) {
    onMouseMoveRef.current = (e) => {
      if (!draggingRef.current) return;
      const { onResize: cb, side: s, minWidth: minW, maxWidth: maxW } = cfgRef.current;
      const desired = s === 'right' ? window.innerWidth - e.clientX : e.clientX;
      const clamped = Math.max(minW, Math.min(maxW, desired));
      cb?.(clamped);
    };
  }
  if (!releaseRef.current) {
    releaseRef.current = () => {
      window.removeEventListener('mousemove', onMouseMoveRef.current);
      window.removeEventListener('mouseup', onMouseUpRef.current);
    };
  }
  if (!onMouseUpRef.current) {
    onMouseUpRef.current = () => {
      if (!draggingRef.current) return;
      draggingRef.current = false;
      setIsDragging(false);
      setDragCursor(false);
      releaseRef.current?.();
    };
  }

  const onMouseDown = useCallback((e) => {
    e.preventDefault();
    draggingRef.current = true;
    setIsDragging(true);
    setDragCursor(true);
    window.addEventListener('mousemove', onMouseMoveRef.current);
    window.addEventListener('mouseup', onMouseUpRef.current);
  }, [setDragCursor]);

  // Always release listeners on unmount in case the user drags then the
  // panel collapses or the page navigates away mid-drag. The cleanup runs
  // ONLY on unmount now (empty deps) — no chance of mid-drag tear-down.
  useEffect(() => () => {
    if (draggingRef.current) {
      draggingRef.current = false;
      setDragCursor(false);
    }
    releaseRef.current?.();
  }, [setDragCursor]);

  // Inline styles for width, positioning, and cursor — the pre-built
  // clinic.css doesn't include `w-[5px]`, `inset-y-0`, `left-1/2`,
  // `-translate-x-1/2`, or `group-hover:bg-*` utilities, so a Tailwind-only
  // implementation rendered 0px wide and invisible.
  const [hovered, setHovered] = useState(false);
  return (
    <>
      <div
        role="separator"
        aria-orientation="vertical"
        onMouseDown={onMouseDown}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        title="Drag to resize"
        className="shrink-0 cursor-ew-resize"
        style={{
          width: '6px',
          position: 'relative',
          background: 'transparent',
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: '50%',
            transform: 'translateX(-50%)',
            width: hovered || isDragging ? '3px' : '1px',
            background: hovered || isDragging ? 'rgb(96 165 250)' /* blue-400 */ : 'rgb(203 213 225)' /* slate-300 */,
            transition: 'width 80ms ease, background 80ms ease',
          }}
        />
      </div>
      {/* Drag overlay — iframes (e.g. PDF preview in the Reports tab) and
          contenteditable surfaces capture mouse events when the cursor
          crosses them, which kills the splitter's `window` mousemove
          listener (the user reported being able to expand but not shrink
          past a PDF). A transparent full-viewport overlay during drag sits
          ON TOP of every iframe so mouse events keep reaching the parent
          document. The overlay also enforces the resize cursor across the
          whole screen so it doesn't flicker when crossing other widgets. */}
      {isDragging && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            cursor: 'ew-resize',
            zIndex: 9999,
            background: 'transparent',
            // Belt-and-suspenders: even if body's userSelect is somehow
            // overridden, the overlay itself swallows selection attempts
            // when the cursor crosses tab labels / body text mid-drag.
            userSelect: 'none',
            WebkitUserSelect: 'none',
          }}
          // Some browsers still try to start a selection on the topmost
          // element under the cursor; preventDefault on selectstart kills
          // any residual highlight requests that slip through.
          onSelectStart={(e) => e.preventDefault()}
        />
      )}
    </>
  );
}
